import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Database, Plus, Pencil, Trash2, Store, Truck, Warehouse } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  AggHeader, ResetSampleButton, Panel, useSort, SortTh, TH_ROW, TD, inr, inrCompact, num, Pill, TableFilter, textMatch,
  FieldSelect, clearAggMeta, useAggMeta,
} from "./aggKit";

const REGIONS = ["North", "West", "South", "East", "North-East", "Central"];
const STATUS_OPTS = [["active", "Active"], ["paused", "Paused"], ["inactive", "Inactive"]];

const CONFIG = {
  merchants: {
    label: "Merchants (MSMEs)", single: "merchant", icon: Store, filterParam: "merchant_id",
    search: ["name", "contact_name", "city", "category", "gstin", "email"],
    columns: [
      ["name", "Merchant", (r) => <><div className="font-semibold">{r.name}</div><div className="text-[10px] text-muted-foreground">{r.category}</div></>],
      ["contact_name", "Contact", (r) => <><div>{r.contact_name}</div><div className="text-[10px] text-muted-foreground">{r.phone}</div></>],
      ["city", "City", (r) => `${r.city || "—"}${r.state ? `, ${r.state}` : ""}`],
      ["gstin", "GSTIN", (r) => <span className="font-mono-num">{r.gstin || "—"}</span>],
      ["bank_name", "Payout bank", (r) => <><div>{r.bank_name || "—"}</div><div className="text-[10px] text-muted-foreground font-mono-num">{r.bank_account} · {r.ifsc}</div></>],
      ["cod_remit_cycle_days", "COD cycle", (r) => `T+${r.cod_remit_cycle_days ?? "—"}`, "right"],
    ],
    fields: [
      ["name", "Business name *"], ["category", "Category"], ["contact_name", "Contact person"], ["phone", "Phone"], ["email", "Email"],
      ["city", "City"], ["state", "State"], ["gstin", "GSTIN"], ["bank_name", "Bank name"], ["bank_account", "Bank account no. (stored masked)"],
      ["ifsc", "IFSC"], ["cod_remit_cycle_days", "COD remittance cycle (days)", "number"], ["home_hub_id", "Home franchise hub", "hub"], ["status", "Status", "status"],
    ],
  },
  carriers: {
    label: "3PL carriers", single: "carrier", icon: Truck, filterParam: "carrier_id",
    search: ["name", "code", "mode", "contact_email"],
    columns: [
      ["name", "Carrier", (r) => <><div className="font-semibold">{r.name}</div><div className="text-[10px] text-muted-foreground">{r.code} · {r.mode}</div></>],
      ["rate_per_kg", "Rate / kg", (r) => inr(r.rate_per_kg, 2), "right"],
      ["base_fee", "Base fee", (r) => inr(r.base_fee, 2), "right"],
      ["cod_fee_pct", "COD fee", (r) => `${r.cod_fee_pct}%`, "right"],
      ["sla_days", "SLA", (r) => `${r.sla_days}d`, "right"],
      ["rating", "Rating", (r) => (r.rating ? `★ ${r.rating}` : "—"), "right"],
    ],
    fields: [
      ["name", "Carrier name *"], ["code", "AWB prefix code"], ["mode", "Mode (Surface / Air…)"], ["rate_per_kg", "Rate per kg (₹)", "number"],
      ["base_fee", "Base fee per parcel (₹)", "number"], ["cod_fee_pct", "COD handling fee (%)", "number"], ["sla_days", "Base SLA (days)", "number"],
      ["rating", "Rating (0-5)", "number"], ["contact_email", "Ops contact email"], ["status", "Status", "status"],
    ],
  },
  hubs: {
    label: "Franchise hubs", single: "hub", icon: Warehouse, filterParam: "hub_id",
    search: ["name", "code", "city", "franchisee_name", "region"],
    columns: [
      ["name", "Hub", (r) => <><div className="font-semibold">{r.name}</div><div className="text-[10px] text-muted-foreground">{r.code}</div></>],
      ["franchisee_name", "Franchisee", (r) => <><div>{r.franchisee_name}</div><div className="text-[10px] text-muted-foreground">{r.phone}</div></>],
      ["city", "City", (r) => `${r.city}, ${r.state}`],
      ["region", "Region", (r) => r.region],
      ["commission_per_parcel", "Commission", (r) => `${inr(r.commission_per_parcel, 2)}/parcel`, "right"],
      ["daily_capacity", "Capacity", (r) => `${num(r.daily_capacity)}/day`, "right"],
    ],
    fields: [
      ["name", "Hub name *"], ["code", "Hub code"], ["franchisee_name", "Franchisee"], ["phone", "Phone"], ["city", "City"], ["state", "State"],
      ["region", "Region", "region"], ["commission_per_parcel", "Commission per parcel (₹)", "number"], ["daily_capacity", "Daily capacity (parcels)", "number"],
      ["status", "Status", "status"],
    ],
  },
};

const STATUS_STYLE = {
  active: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
  paused: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30",
  inactive: "bg-slate-500/15 text-slate-600 dark:text-slate-300 border-slate-500/30",
};

function MasterDialog({ kind, open, editing, onClose, onSaved, hubs }) {
  const cfg = CONFIG[kind];
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setF(editing ? { ...editing } : { status: "active", ...(kind === "hubs" ? { region: "West" } : {}) });
  }, [open, editing, kind]);
  const save = async () => {
    if (!String(f.name || "").trim()) { toast.error("Name is required"); return; }
    const body = {};
    cfg.fields.forEach(([k, , t]) => {
      const v = f[k];
      if (v === undefined || v === "") return;
      body[k] = t === "number" ? Number(v) : v;
    });
    setBusy(true);
    try {
      const r = editing ? await api.patch(`/agg/${kind}/${editing.id}`, body) : await api.post(`/agg/${kind}`, body);
      toast.success(editing ? "Saved" : `${cfg.single[0].toUpperCase() + cfg.single.slice(1)} added`);
      clearAggMeta();
      onSaved(r.data); onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={`master-dialog-${kind}`}>
        <DialogHeader>
          <DialogTitle>{editing ? `Edit ${editing.name}` : `Add ${cfg.single}`}</DialogTitle>
          <DialogDescription>{kind === "carriers" ? "The rate card is used to work out 3PL cost on every new or edited parcel." : kind === "hubs" ? "Commission is the franchise payout for each parcel the hub takes in." : "COD collected for this MSME is remitted to the bank account below."}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {cfg.fields.map(([k, label, t]) => (
            <div key={k} className="space-y-1">
              <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</Label>
              {t === "status" ? (
                <FieldSelect testid={`mf-${k}`} className="w-full" value={f[k] || "active"} onChange={(v) => setF((x) => ({ ...x, [k]: v }))}>{STATUS_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</FieldSelect>
              ) : t === "region" ? (
                <FieldSelect testid={`mf-${k}`} className="w-full" value={f[k] || "West"} onChange={(v) => setF((x) => ({ ...x, [k]: v }))}>{REGIONS.map((r) => <option key={r}>{r}</option>)}</FieldSelect>
              ) : t === "hub" ? (
                <FieldSelect testid={`mf-${k}`} className="w-full" value={f[k] || ""} onChange={(v) => setF((x) => ({ ...x, [k]: v }))}><option value="">—</option>{hubs.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}</FieldSelect>
              ) : (
                <Input data-testid={`mf-${k}`} type={t === "number" ? "number" : "text"} min={t === "number" ? 0 : undefined} step={t === "number" ? "any" : undefined}
                  className="rounded-none h-9" value={f[k] ?? ""} onChange={(e) => setF((x) => ({ ...x, [k]: e.target.value }))} />
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" disabled={busy} onClick={save} data-testid="mf-save">{busy ? "Saving…" : "Save"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AggMastersPage() {
  const [kind, setKind] = useState("merchants");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [dlg, setDlg] = useState({ open: false, editing: null });
  const [tick, setTick] = useState(0);
  const meta = useAggMeta(tick);
  const cfg = CONFIG[kind];

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/agg/${kind}`).then((r) => setRows(r.data)).catch((e) => toast.error(apiErr(e))).finally(() => setLoading(false));
  }, [kind]);
  useEffect(() => { load(); }, [load, tick]);
  useEffect(() => { setQ(""); setStatus(""); }, [kind]);

  const filtered = useMemo(() => rows.filter((r) => (!status || r.status === status) && textMatch(r, q, cfg.search)), [rows, q, status, cfg]);
  const sort = useSort(filtered, "gmv");

  const del = async (r) => {
    if (!window.confirm(`Delete ${r.name}?`)) return;
    try { await api.delete(`/agg/${kind}/${r.id}`); toast.success("Deleted"); clearAggMeta(); setTick((t) => t + 1); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-4" data-testid="agg-masters-page">
      <AggHeader icon={Database} title="Network Masters" subtitle="Manage the MSMEs, 3PL carriers and franchise hubs on the platform."
        actions={<>
          <ResetSampleButton onDone={() => { clearAggMeta(); setTick((t) => t + 1); }} />
          <button data-testid="master-add" onClick={() => setDlg({ open: true, editing: null })} className="h-8 px-3 bg-primary text-primary-foreground text-xs font-bold uppercase tracking-wide inline-flex items-center gap-1.5"><Plus className="h-3.5 w-3.5" /> Add {cfg.single}</button>
        </>} />

      <div className="flex items-center gap-1 flex-wrap">
        {Object.entries(CONFIG).map(([k, c]) => (
          <button key={k} onClick={() => setKind(k)} data-testid={`master-tab-${k}`} className={`px-3 py-2 text-xs font-bold uppercase tracking-wide border inline-flex items-center gap-1.5 ${kind === k ? "bg-[#0B1220] text-white border-[#0B1220] dark:bg-primary dark:border-primary" : "border-border hover:bg-muted"}`}>
            <c.icon className="h-3.5 w-3.5" /> {c.label}
          </button>
        ))}
        <div className="flex-1" />
        <FieldSelect testid="master-status" value={status} onChange={setStatus} className="h-8 text-xs"><option value="">All statuses</option>{STATUS_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</FieldSelect>
        <TableFilter testid="master-search" value={q} onChange={setQ} placeholder={`Filter ${cfg.label.toLowerCase()}…`} />
      </div>

      <Panel testid={`master-table-${kind}`} bodyClass="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className={TH_ROW}>
            <tr>
              {cfg.columns.map(([k, l, , align]) => <SortTh key={k} k={k} label={l} sort={sort} align={align} />)}
              <SortTh k="shipments" label="Parcels" sort={sort} align="right" />
              <SortTh k="open_shipments" label="Open" sort={sort} align="right" />
              <SortTh k="gmv" label="GMV" sort={sort} align="right" />
              <SortTh k="revenue" label="Revenue" sort={sort} align="right" />
              <SortTh k="status" label="Status" sort={sort} />
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {!loading && sort.sorted.length === 0 && <tr><td colSpan={cfg.columns.length + 6} className="p-8 text-center text-muted-foreground">Nothing here yet.</td></tr>}
            {sort.sorted.map((r) => (
              <tr key={r.id} className="hover:bg-muted/40" data-testid={`master-row-${r.id}`}>
                {cfg.columns.map(([k, , render, align]) => <td key={k} className={`${TD} ${align === "right" ? "text-right font-mono-num" : ""}`}>{render(r)}</td>)}
                <td className={`${TD} text-right font-mono-num`}><Link className="hover:text-primary underline-offset-2 hover:underline" to={`/dashboard/agg/shipments?${cfg.filterParam}=${r.id}`}>{num(r.shipments)}</Link></td>
                <td className={`${TD} text-right font-mono-num`}>{num(r.open_shipments)}</td>
                <td className={`${TD} text-right font-mono-num`}>{inrCompact(r.gmv)}</td>
                <td className={`${TD} text-right font-mono-num`}>{inrCompact(r.revenue)}</td>
                <td className={TD}><Pill className={STATUS_STYLE[r.status] || STATUS_STYLE.inactive}>{r.status}</Pill></td>
                <td className={TD}>
                  <div className="flex gap-1">
                    <button title="Edit" data-testid={`master-edit-${r.id}`} onClick={() => setDlg({ open: true, editing: r })} className="h-7 w-7 grid place-items-center border border-border hover:bg-muted"><Pencil className="h-3 w-3" /></button>
                    <button title="Delete" data-testid={`master-del-${r.id}`} onClick={() => del(r)} className="h-7 w-7 grid place-items-center border border-border hover:bg-destructive hover:text-destructive-foreground"><Trash2 className="h-3 w-3" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>

      <MasterDialog kind={kind} open={dlg.open} editing={dlg.editing} hubs={meta?.hubs || []} onClose={() => setDlg({ open: false, editing: null })} onSaved={() => setTick((t) => t + 1)} />
    </div>
  );
}
